import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/components/ui/Toast';
import { apiClient, type ApiResponse } from '@/lib/api-client';
import { exportRowsToCsv } from '@/lib/utils';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { useInvoicesList, useQuotesList, useDebitNotesList } from './hooks/useBilling';
import { useWriteOffs } from './hooks/useWriteOffs';
import { useDiscounts } from './hooks/useDiscounts';
import { useBillingEntityConfigs } from './hooks/useBillingEntityConfig';
import { GroupInvoicePicker } from '@/features/contact-groups';
import {
  TRANSACTION_TYPE_LABELS,
  type BillingTransactionType,
} from './hooks/useBillingTransaction';

// ─── Unified row type ────────────────────────────────────────────────────────

interface TransactionRow {
  id: string;
  type: BillingTransactionType;
  number: string;
  clientName: string;
  partnerName: string;
  amount: number;
  balance: number;
  status: string;
  isBadDebt?: boolean;
  practiceEntityId?: string | null;
  date: string;
  dueDate?: string | null;
  createdAt: string;
}

const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  sent: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  viewed: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  partially_paid: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  paid: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  void: 'bg-gray-200 text-gray-500 dark:bg-gray-600 dark:text-gray-400',
  applied: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  voided: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  issued: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
};

/**
 * BillingTransactionsListPage — unified list of all billing transactions.
 * Fetches invoices, write-offs, and discounts in parallel and merges into
 * a single table. Filter by transaction type, search by number/client.
 */
export function BillingTransactionsListPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { toast } = useToast();

  const [typeFilter, setTypeFilter] = useState<BillingTransactionType | ''>('');
  const [search, setSearch] = useState('');
  const [badDebtOnly, setBadDebtOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [showGroupInvoice, setShowGroupInvoice] = useState(false);

  // ─── Advanced filters ─────────────────────────────────────────────────────
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState(''); // practiceEntityId
  const [partnerFilter, setPartnerFilter] = useState(''); // partner name
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [balanceMin, setBalanceMin] = useState('');
  const [balanceMax, setBalanceMax] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data: entityConfigs } = useBillingEntityConfigs();
  const pageSize = 25;

  const canDownload = can(PERMISSIONS.DOCUMENTS_READ);

  /**
   * Downloads (generates) the PDF for a transaction row. Mirrors the per-type
   * generate-pdf endpoints used by the detail view; opens the returned URL.
   */
  const handleDownload = useCallback(
    async (row: TransactionRow) => {
      const pathByType: Partial<Record<BillingTransactionType, string>> = {
        invoice: 'invoices',
        quote: 'quotes',
        credit_note: 'credit-notes',
        debit_note: 'debit-notes',
        write_off: 'write-offs',
        advance: 'advances',
      };
      const path = pathByType[row.type];
      if (!path) return;

      setDownloadingId(row.id);
      try {
        const { data } = await apiClient.post<ApiResponse<{ downloadUrl: string }>>(
          `/billing/${path}/${row.id}/generate-pdf`,
          row.type === 'invoice' ? { async: false } : {},
        );
        if (data.data?.downloadUrl) {
          window.open(data.data.downloadUrl, '_blank');
          toast({ variant: 'success', title: 'PDF generated' });
        }
      } catch {
        toast({
          variant: 'error',
          title: 'Failed to generate PDF. Ensure a default template is configured.',
        });
      } finally {
        setDownloadingId(null);
      }
    },
    [toast],
  );

  /** Whether a row's status makes a PDF meaningful (mirrors the detail view). */
  function isDownloadable(row: TransactionRow): boolean {
    switch (row.type) {
      case 'invoice':
        return ['sent', 'viewed', 'partially_paid', 'overdue', 'paid'].includes(row.status);
      case 'quote':
        return ['sent', 'accepted'].includes(row.status);
      case 'credit_note':
        return row.status === 'applied';
      case 'debit_note':
        return row.status === 'issued';
      case 'write_off':
        return row.status === 'applied';
      case 'advance':
        return row.status === 'received';
      default:
        return false;
    }
  }

  // ─── Fetch data for each type in parallel ───────────────────────────────
  const { data: invoicesData, isLoading: invLoading } = useInvoicesList({
    page: 1,
    pageSize: 100,
    search: search || undefined,
    badDebt: badDebtOnly || undefined,
  });
  const { data: quotesData, isLoading: quoLoading } = useQuotesList({
    page: 1,
    pageSize: 100,
  });
  const {
    data: debitNotesData,
    isLoading: dnLoading,
    isError: dnError,
  } = useDebitNotesList({
    page: 1,
    pageSize: 100,
  });
  const {
    data: writeOffsData,
    isLoading: woLoading,
    isError: woError,
  } = useWriteOffs({
    page: 1,
    pageSize: 100,
  });
  const {
    data: discountsData,
    isLoading: discLoading,
    isError: discError,
  } = useDiscounts({
    page: 1,
    pageSize: 100,
  });

  const {
    data: advancesData,
    isLoading: advLoading,
    isError: advError,
  } = useQuery({
    queryKey: ['billing', 'advances', 'list'],
    queryFn: async () => {
      const response = await apiClient.get<
        ApiResponse<{ advances: Array<Record<string, unknown>> }>
      >('/billing/advances?pageSize=100');
      return response.data.data;
    },
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const {
    data: creditNotesData,
    isLoading: cnLoading,
    isError: cnError,
  } = useQuery({
    queryKey: ['billing', 'credit-notes', 'list'],
    queryFn: async () => {
      const response = await apiClient.get<{
        success: boolean;
        data: Array<Record<string, unknown>>;
      }>('/billing/credit-notes');
      return response.data.data ?? [];
    },
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const isLoading =
    invLoading ||
    quoLoading ||
    (dnLoading && !dnError) ||
    (woLoading && !woError) ||
    (discLoading && !discError) ||
    (advLoading && !advError) ||
    (cnLoading && !cnError);

  // ─── Merge and normalise ─────────────────────────────────────────────────
  const allRows: TransactionRow[] = [];

  for (const inv of invoicesData?.invoices ?? []) {
    allRows.push({
      id: inv.id,
      type: 'invoice',
      number: inv.invoiceNumber,
      clientName: inv.clientName || '',
      partnerName: inv.createdByName || '',
      isBadDebt: inv.isBadDebt,
      practiceEntityId:
        ((inv as unknown as Record<string, unknown>).practiceEntityId as string | undefined) ||
        null,
      amount: inv.total,
      balance: inv.outstandingBalance,
      status: inv.status,
      date: inv.issueDate,
      dueDate: inv.dueDate,
      createdAt: inv.createdAt,
    });
  }
  for (const q of quotesData?.quotes ?? []) {
    allRows.push({
      id: q.id,
      type: 'quote',
      number: q.quoteNumber,
      clientName: q.clientName || '',
      partnerName: q.createdByName || '',
      amount: q.total,
      balance: q.total,
      status: q.status,
      date: q.issueDate || q.createdAt,
      dueDate: q.expiryDate,
      createdAt: q.createdAt,
    });
  }
  for (const dn of debitNotesData?.debitNotes ?? []) {
    allRows.push({
      id: dn.id,
      type: 'debit_note',
      number: `DN-${dn.id.slice(0, 8)}`,
      clientName: dn.clientName || '',
      partnerName: dn.createdByName || '',
      amount: dn.amount,
      balance: dn.status === 'issued' ? 0 : dn.amount,
      status: dn.status,
      date: dn.transactionDate,
      createdAt: dn.createdAt,
    });
  }
  for (const wo of writeOffsData?.writeOffs ?? []) {
    allRows.push({
      id: wo.id,
      type: 'write_off',
      number: wo.writeOffNumber,
      clientName: wo.clientName || '',
      partnerName: wo.createdByName || '',
      practiceEntityId: wo.practiceEntityId || null,
      amount: wo.amount,
      balance: 0,
      status: wo.status,
      date: wo.transactionDate,
      createdAt: wo.createdAt || wo.transactionDate,
    });
  }
  for (const d of discountsData?.discounts ?? []) {
    allRows.push({
      id: d.id,
      type: 'discount',
      number: d.discountNumber,
      clientName: d.clientName || '',
      partnerName: d.createdByName || '',
      practiceEntityId: d.practiceEntityId || null,
      amount: d.amount,
      balance: 0,
      status: d.status,
      date: d.transactionDate,
      createdAt: d.transactionDate,
    });
  }
  for (const a of advancesData?.advances ?? []) {
    allRows.push({
      id: a.id as string,
      type: 'advance',
      number: (a.advance_number || '') as string,
      clientName: (a.client_name || '') as string,
      partnerName: (a.created_by_name || '') as string,
      practiceEntityId: (a.practice_entity_id || null) as string | null,
      amount: Number(a.total || 0),
      balance: Number(a.available_balance || 0),
      status: (a.status || 'draft') as string,
      date: (a.transaction_date || a.created_at || '') as string,
      createdAt: (a.created_at || '') as string,
    });
  }
  for (const cn of creditNotesData ?? []) {
    allRows.push({
      id: (cn.id || '') as string,
      type: 'credit_note',
      number: (cn.credit_note_number || cn.creditNoteNumber || '') as string,
      clientName: (cn.client_name || cn.clientName || '') as string,
      partnerName: (cn.created_by_name || cn.createdByName || '') as string,
      amount: Number(cn.amount || 0),
      balance: 0,
      status: (cn.status || 'applied') as string,
      date: (cn.created_at || cn.createdAt || '') as string,
      createdAt: (cn.created_at || cn.createdAt || '') as string,
    });
  }

  // Distinct values for advanced-filter dropdowns (derived from loaded rows).
  const statusOptions = Array.from(new Set(allRows.map((r) => r.status).filter(Boolean))).sort();
  const partnerOptions = Array.from(
    new Set(allRows.map((r) => r.partnerName).filter(Boolean)),
  ).sort();

  const amountMinNum = amountMin ? parseFloat(amountMin) : null;
  const amountMaxNum = amountMax ? parseFloat(amountMax) : null;
  const balanceMinNum = balanceMin ? parseFloat(balanceMin) : null;
  const balanceMaxNum = balanceMax ? parseFloat(balanceMax) : null;

  const activeAdvancedCount = [
    statusFilter,
    entityFilter,
    partnerFilter,
    amountMin,
    amountMax,
    balanceMin,
    balanceMax,
    dateFrom,
    dateTo,
  ].filter(Boolean).length;

  // ─── Filter and sort ─────────────────────────────────────────────────────
  const filtered = allRows
    // Bad-debt only shows flagged invoices (bad debt applies to invoices only).
    .filter((r) => !badDebtOnly || (r.type === 'invoice' && r.isBadDebt))
    .filter((r) => !typeFilter || r.type === typeFilter)
    .filter((r) => !statusFilter || r.status === statusFilter)
    .filter((r) => !entityFilter || r.practiceEntityId === entityFilter)
    .filter((r) => !partnerFilter || r.partnerName === partnerFilter)
    .filter((r) => amountMinNum === null || r.amount >= amountMinNum)
    .filter((r) => amountMaxNum === null || r.amount <= amountMaxNum)
    .filter((r) => balanceMinNum === null || r.balance >= balanceMinNum)
    .filter((r) => balanceMaxNum === null || r.balance <= balanceMaxNum)
    .filter((r) => !dateFrom || (r.date && r.date.slice(0, 10) >= dateFrom))
    .filter((r) => !dateTo || (r.date && r.date.slice(0, 10) <= dateTo))
    .filter((r) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        r.number.toLowerCase().includes(q) ||
        r.clientName.toLowerCase().includes(q) ||
        r.partnerName.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());

  const clearAllFilters = () => {
    setSearch('');
    setTypeFilter('');
    setBadDebtOnly(false);
    setStatusFilter('');
    setEntityFilter('');
    setPartnerFilter('');
    setAmountMin('');
    setAmountMax('');
    setBalanceMin('');
    setBalanceMax('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  // Resolve a readable practice-entity name for export.
  const entityNameById = new Map(
    (entityConfigs || []).map((c) => [
      c.practiceEntityId,
      c.billingEntityName || c.practiceEntityCode || c.practiceEntityId,
    ]),
  );

  // Whether the user may export (reuses the billing ledger-export permission).
  const canExport = can(PERMISSIONS.BILLING_EXPORT_LEDGER);

  /** Export the currently-filtered rows to a CSV file (opens in Excel). */
  const handleExport = useCallback(() => {
    if (filtered.length === 0) {
      toast({
        variant: 'error',
        title: 'Nothing to export',
        description: 'No transactions match the current filters.',
      });
      return;
    }
    exportRowsToCsv(
      `billing-transactions-${new Date().toISOString().slice(0, 10)}`,
      [
        { header: 'Type', value: (r: TransactionRow) => TRANSACTION_TYPE_LABELS[r.type] },
        { header: 'Number', value: (r: TransactionRow) => r.number },
        { header: 'Client', value: (r: TransactionRow) => r.clientName },
        { header: 'Partner', value: (r: TransactionRow) => r.partnerName },
        {
          header: 'Practice Entity',
          value: (r: TransactionRow) =>
            r.practiceEntityId ? entityNameById.get(r.practiceEntityId) || r.practiceEntityId : '',
        },
        {
          header: 'Date',
          value: (r: TransactionRow) => (r.date ? new Date(r.date).toLocaleDateString() : ''),
        },
        {
          header: 'Due Date',
          value: (r: TransactionRow) => (r.dueDate ? new Date(r.dueDate).toLocaleDateString() : ''),
        },
        { header: 'Amount', value: (r: TransactionRow) => r.amount.toFixed(2) },
        { header: 'Balance', value: (r: TransactionRow) => r.balance.toFixed(2) },
        { header: 'Status', value: (r: TransactionRow) => r.status.replace(/_/g, ' ') },
        { header: 'Bad Debt', value: (r: TransactionRow) => (r.isBadDebt ? 'Yes' : 'No') },
      ],
      filtered,
    );
    toast({ variant: 'success', title: `Exported ${filtered.length} transaction(s)` });
  }, [filtered, entityNameById, toast]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleSearch = useCallback((v: string) => {
    setSearch(v);
    setPage(1);
  }, []);
  const handleType = useCallback((v: string) => {
    setTypeFilter(v as BillingTransactionType | '');
    setPage(1);
  }, []);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Billing Transactions
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            All invoices, write-offs, discounts and more
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canExport && (
            <Button
              variant="outline"
              onClick={handleExport}
              title="Export the filtered list to Excel (CSV)"
            >
              <svg
                className="-ml-0.5 mr-1.5 h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                />
              </svg>
              Export to Excel
            </Button>
          )}
          {can(PERMISSIONS.GROUPS_MANAGE_INVOICING) && (
            <Button variant="outline" onClick={() => setShowGroupInvoice(true)}>
              New Group Invoice
            </Button>
          )}
          {can(PERMISSIONS.BILLING_CREATE_INVOICE) && (
            <Button onClick={() => navigate('/billing/new')}>Create Transaction</Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent>
          {/* Primary row: Type + Bad Debt sit together, then search + actions */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-48">
              <Select
                label="Transaction Type"
                value={typeFilter}
                onChange={(e) => handleType(e.target.value)}
                options={[
                  { value: '', label: 'All Types' },
                  ...(can(PERMISSIONS.BILLING_CREATE_INVOICE)
                    ? [{ value: 'invoice', label: 'Invoices' }]
                    : []),
                  ...(can(PERMISSIONS.BILLING_MANAGE_QUOTES)
                    ? [{ value: 'quote', label: 'Quotes' }]
                    : []),
                  ...(can(PERMISSIONS.BILLING_CREATE_WRITE_OFF)
                    ? [{ value: 'write_off', label: 'Write-Offs' }]
                    : []),
                  ...(can(PERMISSIONS.BILLING_CREATE_DEBIT_NOTE)
                    ? [{ value: 'debit_note', label: 'Debit Notes' }]
                    : []),
                  ...(can(PERMISSIONS.BILLING_CREATE_ADVANCE)
                    ? [{ value: 'advance', label: 'Advances' }]
                    : []),
                  ...(can(PERMISSIONS.BILLING_CREATE_CREDIT_NOTE)
                    ? [{ value: 'credit_note', label: 'Credit Notes' }]
                    : []),
                ]}
              />
            </div>
            {/* Bad Debt toggle — next to Transaction Type */}
            <Button
              variant={badDebtOnly ? 'danger' : 'outline'}
              onClick={() => {
                setBadDebtOnly((v) => !v);
                setPage(1);
              }}
              title="Show only invoices marked as bad debt"
              className="h-[38px]"
            >
              {badDebtOnly ? 'Bad Debt: On' : 'Bad Debt'}
            </Button>

            <div className="flex-1" />

            <Input
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search number, client, or partner..."
              className="w-64"
            />
            <Button
              variant="outline"
              onClick={() => setShowAdvanced((v) => !v)}
              className="h-[38px]"
            >
              Filters{activeAdvancedCount > 0 ? ` (${activeAdvancedCount})` : ''}
            </Button>
            {(search || badDebtOnly || typeFilter || activeAdvancedCount > 0) && (
              <Button variant="outline" onClick={clearAllFilters} className="h-[38px]">
                Clear
              </Button>
            )}
          </div>

          {/* Advanced filter panel */}
          {showAdvanced && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-border pt-4 dark:border-border-dark sm:grid-cols-3">
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                options={[
                  { value: '', label: 'All Statuses' },
                  ...statusOptions.map((s) => ({
                    value: s,
                    label: s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
                  })),
                ]}
              />
              <Select
                label="Practice Entity"
                value={entityFilter}
                onChange={(e) => {
                  setEntityFilter(e.target.value);
                  setPage(1);
                }}
                options={[
                  { value: '', label: 'All Entities' },
                  ...(entityConfigs || []).map((c) => ({
                    value: c.practiceEntityId,
                    label: c.billingEntityName || c.practiceEntityCode || c.practiceEntityId,
                  })),
                ]}
              />
              <Select
                label="Partner"
                value={partnerFilter}
                onChange={(e) => {
                  setPartnerFilter(e.target.value);
                  setPage(1);
                }}
                options={[
                  { value: '', label: 'All Partners' },
                  ...partnerOptions.map((p) => ({ value: p, label: p })),
                ]}
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  label="Amount Min"
                  type="number"
                  value={amountMin}
                  onChange={(e) => {
                    setAmountMin(e.target.value);
                    setPage(1);
                  }}
                  placeholder="0.00"
                />
                <Input
                  label="Amount Max"
                  type="number"
                  value={amountMax}
                  onChange={(e) => {
                    setAmountMax(e.target.value);
                    setPage(1);
                  }}
                  placeholder="0.00"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  label="Balance Min"
                  type="number"
                  value={balanceMin}
                  onChange={(e) => {
                    setBalanceMin(e.target.value);
                    setPage(1);
                  }}
                  placeholder="0.00"
                />
                <Input
                  label="Balance Max"
                  type="number"
                  value={balanceMax}
                  onChange={(e) => {
                    setBalanceMax(e.target.value);
                    setPage(1);
                  }}
                  placeholder="0.00"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  label="Date From"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setPage(1);
                  }}
                />
                <Input
                  label="Date To"
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
            </div>
          ) : paged.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-500">
              No transactions found
              {typeFilter ? ` for type "${TRANSACTION_TYPE_LABELS[typeFilter]}"` : ''}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs font-medium uppercase text-gray-500 dark:border-border-dark dark:text-gray-400">
                    <th className="px-3 py-3">Type</th>
                    <th className="px-3 py-3">Number</th>
                    <th className="px-3 py-3">Client</th>
                    <th className="px-3 py-3">Partner</th>
                    <th className="px-3 py-3">Date</th>
                    <th className="px-3 py-3">Due Date</th>
                    <th className="px-3 py-3 text-right">Amount</th>
                    <th className="px-3 py-3 text-right">Balance</th>
                    <th className="px-3 py-3">Status</th>
                    <th className="px-3 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {paged.map((row) => (
                    <tr
                      key={`${row.type}-${row.id}`}
                      className="border-b border-border last:border-0 dark:border-border-dark"
                    >
                      <td className="px-3 py-3">
                        <span className="inline-flex rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-900/20 dark:text-brand-300">
                          {TRANSACTION_TYPE_LABELS[row.type]}
                        </span>
                      </td>
                      <td className="px-3 py-3 font-medium">{row.number}</td>
                      <td className="px-3 py-3 text-gray-500">{row.clientName || '—'}</td>
                      <td className="px-3 py-3 text-gray-500">{row.partnerName || '—'}</td>
                      <td className="px-3 py-3">
                        {row.date ? new Date(row.date).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-3 py-3 text-gray-500">
                        {row.dueDate ? new Date(row.dueDate).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-3 py-3 text-right font-medium">
                        {row.amount.toLocaleString('en', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td
                        className={`px-3 py-3 text-right font-medium ${row.balance > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}
                      >
                        {row.balance.toLocaleString('en', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap items-center gap-1">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[row.status] ?? 'bg-gray-100 text-gray-600'}`}
                          >
                            {row.status.replace(/_/g, ' ')}
                          </span>
                          {row.isBadDebt && (
                            <span className="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400">
                              Bad Debt
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/billing/${row.id}?type=${row.type}`)}
                          >
                            View
                          </Button>
                          {canDownload && isDownloadable(row) && (
                            <Button
                              variant="outline"
                              size="sm"
                              loading={downloadingId === row.id}
                              disabled={downloadingId === row.id}
                              onClick={() => handleDownload(row)}
                              aria-label={`Download ${TRANSACTION_TYPE_LABELS[row.type]} PDF`}
                              title="Download PDF"
                            >
                              <svg
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                aria-hidden="true"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                                />
                              </svg>
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Page {page} of {totalPages} ({filtered.length} total)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {showGroupInvoice && <GroupInvoicePicker onClose={() => setShowGroupInvoice(false)} />}
    </div>
  );
}
